import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users2, MessageSquare, Star, FileText, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { DocumentActionsMenu } from "@/components/document-actions-menu";
import type { DocumentWithCounts, DocumentVersion, DocumentPage } from "@shared/schema";

interface ModernDocumentCardProps {
  document: DocumentWithCounts;
  onSelect: (document: DocumentWithCounts) => void;
  onEdit?: (document: DocumentWithCounts) => void;
  onDelete?: (documentId: string) => void;
}

interface DocumentDetailsResponse {
  document: DocumentWithCounts;
  versions: DocumentVersion[];
  pages: DocumentPage[];
}

export function ModernDocumentCard({ document, onSelect, onEdit, onDelete }: ModernDocumentCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: documentData, isLoading } = useQuery<DocumentDetailsResponse>({
    queryKey: ['/api/documents', document.id],
    enabled: !!document.id,
  });

  const currentVersion = documentData?.versions?.find(v => v.version === document.currentVersion);
  const versionPages = documentData?.pages?.filter(p => p.versionId === currentVersion?.id) || [];
  const firstPage = versionPages.length > 0 ? versionPages[0] : undefined;

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [document.id]);

  const handleCardClick = (e: React.MouseEvent) => {
    if (!isDeleting && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      onSelect(document);
    }
  };

  return (
    <div className="relative">
      <Link 
        href={`/doc/${document.id}`}
        className={`block ${isDeleting ? 'opacity-40 pointer-events-none' : ''}`}
        onClick={handleCardClick}
        data-testid={`card-document-${document.id}`}
      >
        <Card className="group relative overflow-hidden border border-border bg-card hover:border-primary/30 transition-all duration-200 h-64">
          <div className="absolute inset-0">
            {!isLoading && firstPage && !imageError ? (
              <>
                {!imageLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                )}
                <img
                  src={firstPage.webpUrl ?? firstPage.imageUrl ?? undefined}
                  alt={`${document.name} preview`}
                  className={`w-full h-full object-cover transition-opacity duration-200 ${
                    imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => { setImageError(true); setImageLoaded(true); }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
              </>
            ) : (
              <div className="absolute inset-0 bg-muted flex items-center justify-center">
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                ) : (
                  <FileText className="w-10 h-10 text-muted-foreground/40" />
                )}
              </div>
            )}
          </div>

          <div className="relative h-full flex flex-col justify-between p-4">
            <div className="flex justify-between items-start">
              <span className="h-7 w-7 flex items-center justify-center font-mono text-xs text-white/70 bg-black/30 backdrop-blur-sm rounded">
                v{document.currentVersion}
              </span>
              {/* Spacer so the version badge stays left-aligned */}
              <div className="w-7 h-7" />
            </div>

            <div className="space-y-2.5">
              <h3 className="text-sm font-medium text-white line-clamp-2 leading-snug">
                {document.name}
              </h3>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 font-mono text-xs text-white/60">
                  <span className="flex items-center gap-1">
                    <Users2 className="w-3 h-3" />
                    {document.collaboratorCount}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" />
                    {document.commentCount}
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {document.pageCount}
                  </span>
                  {document.averageRating > 0 && (
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" />
                      {document.averageRating}
                    </span>
                  )}
                </div>
                <span className="font-mono text-[11px] text-white/40">
                  {formatDistanceToNow(new Date(document.updatedAt), { addSuffix: true })}
                </span>
              </div>
            </div>
          </div>

          {isDeleting && (
            <div className="absolute inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting...
              </div>
            </div>
          )}
        </Card>
      </Link>

      {/* Actions menu lives outside the <Link> so button-inside-anchor invalid HTML is avoided */}
      <div className="absolute top-3 right-3 z-10">
        <DocumentActionsMenu
          document={document}
          onEdit={onEdit}
          onDelete={onDelete}
          onDeletingStateChange={setIsDeleting}
          className="h-7 w-7 text-white/70 bg-black/30 backdrop-blur-sm hover:text-white hover:bg-black/40 rounded"
          variant="ghost"
          size="icon"
        />
      </div>
    </div>
  );
}

export function ModernDocumentCardSkeleton() {
  return (
    <Card className="overflow-hidden border border-border h-64 bg-muted/50">
      <div className="h-full flex flex-col justify-between p-4">
        <div className="flex justify-end">
          <Skeleton className="h-5 w-10 rounded" />
        </div>
        <div className="space-y-2.5">
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-3 w-2/5" />
          <div className="flex gap-3">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-3 w-8" />
          </div>
        </div>
      </div>
    </Card>
  );
}
